import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { firebase } from "../../services/Firebase";
import { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { getUserOrCreate, updateUser } from "../../services/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CustomUseContext } from "../../utils/storeApi";
import { CustomDialog } from "../../components/CustomDialog";
import { BallLoading } from "../../components/Loading/Ball";

const errorAndsTranslations = {
  'auth/email-already-in-use': 'emailAlreadyInUse',
};

export function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const contextAtt = CustomUseContext();
  const { t, } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [waitingNavitation, setWaitingNavitation] = useState(false);

  useEffect(() => {
    if (contextAtt.user?.id && name) {
      // @ts-ignore
      updateUser(contextAtt.user?.id, { name });
      contextAtt.setUser({
        ...contextAtt.user,
        name,
      })
    }
  }, [contextAtt.user])

  const signupUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    if (password.length < 6) {
      setLoading(false);
      return toast.error(t('Auth.errors.passwordWeak'), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    createUserWithEmailAndPassword(firebase.auth, email, password)
      .then(async (userCredential) => {
        const user = userCredential.user;

        if (user) {
          setWaitingNavitation(true);
        }
      })
      .catch((error) => {
        console.log('error on log user', error)
        const errorCode = error.code;
        const errorMessage = error.message;
        // ..
        console.log("errorCode", errorCode);

        // @ts-ignore
        const translationKey = errorAndsTranslations[error?.code!]

        toast.error(t(`Auth.errors.${translationKey}`), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      })
      .finally(() => {
        setLoading(false);
      })
  }

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-50">
        <body class="h-full">
        ```
      */}
      <CustomDialog 
        open={waitingNavitation} 
        setOpen={setWaitingNavitation} 
        title={t('Auth.accountCreated')} 
        body={() => <>{`${t('Auth.waitingRedirect')}`}</>} 
      />
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h1 className="text-center font-bold text-3xl text-purple-900">Vysen</h1>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
              {t('Auth.orSignupToCreate')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('Common.or')}{' '}
              <Link to="/sign" className="font-medium text-indigo-600 hover:text-indigo-500">
                {t('Auth.signInYourAccount')}
              </Link>
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={signupUser}>
            <input type="hidden" name="remember" defaultValue="true" />
            <div className="rounded-md shadow-sm">
              <div className="mb-6">
                <label htmlFor="name" className="sr-only">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="name"
                  autoComplete="name"
                  required
                  className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                  placeholder={t('Common.name')}
                  onChange={(event) => setName(event.target.value)}
                  value={name}

                />
              </div>
              <div className="mb-6">
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="relative block w-full appearance-none rounded-none rounded-t-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                  placeholder={t('Common.emailAddress')}
                  onChange={(event) => setEmail(event.target.value)}
                  value={email}

                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="relative block w-full appearance-none rounded-none rounded-b-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                  placeholder={t('Common.password')}
                  onChange={(event) => setPassword(event.target.value)}
                  value={password}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-75"
                disabled={loading}
              >
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  {/* <LockClosedIcon className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400" aria-hidden="true" /> */}
                </span>
                {t('Auth.signup')}
                {loading && (
                  <div className="ml-2">
                    <BallLoading height="" />
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
